import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { A11y, Autoplay, EffectFade, Mousewheel, Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Box, IconButton } from '@chakra-ui/react';
import { ChevronRightIcon } from '@udacity/chakra-uds-icons';

import { trackEvent } from '~/features/analytics/track-event';
import { ChevronLeftIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { useClientRender } from '~/hooks/use-client-render';
import { useI18n } from '~/hooks/use-i18n';

import 'swiper/css';
import 'swiper/css/mousewheel';
import 'swiper/css/autoplay';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

export function CardCarousel<
  T extends {
    key: string;
  },
>({ items, renderCard }: { items: T[]; renderCard: ({ item, index }: { item: T; index: number }) => ReactNode }) {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const bannerEventRef = useRef<Record<number, boolean>>({});
  const isClientReady = useClientRender();
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const { i18n, isRtl } = useI18n();
  const cards = useMemo(() => {
    return items.map((item, index) => {
      return <SwiperSlide key={item.key}>{renderCard({ item, index })}</SwiperSlide>;
    });
  }, [items, renderCard]);

  const handleBannerLoad = useCallback(
    (realIndex: number) => {
      const hasFiredEvent = bannerEventRef.current[realIndex];
      if (isIntersecting && !hasFiredEvent) {
        bannerEventRef.current[realIndex] = true;
        trackEvent({
          name: 'Banner Viewed',
          type: 'view',
          label: 'hero_banner_viewed',
          value: items[realIndex].key,
        });
      }
    },
    [items, isIntersecting]
  );

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => {
      setIsIntersecting(entry.isIntersecting);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Box ref={containerRef} position='relative'>
      <Box
        isolation='isolate'
        position='relative'
        sx={{
          '.swiper': {
            opacity: isClientReady ? 1 : 0,
            height: isClientReady ? undefined : '0px',
            paddingBottom: '48px',
          },
          '.swiper-slide': {
            height: 'auto',
            display: 'flex',
          },
          '.swiper-slide > *': {
            width: '100%',
          },
          '.swiper-pagination': {
            height: '30px',
          },
          '.swiper-pagination-bullet': {
            backgroundColor: 'blue.300',
            width: '16px',
            borderRadius: '8px',
            height: '4px',
            opacity: 0.33,
            transition: 'all 0.3s ease',
          },
          '.swiper-pagination-bullet-active': {
            backgroundColor: 'blue.300',
            opacity: 1,
            width: '56px',
            borderRadius: '8px',
            height: '4px',
          },
          // Note: this is a weird hack to make the swiper slide widths responsive.
          '.swiper-wrapper': {
            width: '1px',
          },
        }}
      >
        <Swiper
          key={isClientReady ? 'client' : 'server'}
          direction='horizontal'
          effect='fade'
          longSwipes={false}
          modules={[A11y, Mousewheel, EffectFade, Autoplay, Navigation, Pagination]}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
            stopOnLastSlide: false,
            waitForTransition: true,
          }}
          fadeEffect={{
            crossFade: true,
          }}
          mousewheel={{
            forceToAxis: true,
            releaseOnEdges: true,
            thresholdDelta: 10,
          }}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          pagination={{
            clickable: true,
          }}
          loop
          onBeforeInit={(swiper) => {
            swiper.changeLanguageDirection(i18n.dir());
          }}
          onRealIndexChange={(swiper) => {
            handleBannerLoad(swiper.realIndex);
          }}
        >
          {isClientReady ? cards : cards.slice(0, 1)}
        </Swiper>
      </Box>
      <IconButton
        ref={prevRef}
        aria-label='Previous'
        color='grey.500'
        icon={isRtl ? <ChevronRightIcon height='36px' width='36px' /> : <ChevronLeftIcon height='36px' width='36px' />}
        isDisabled={!isClientReady}
        left='-64px'
        opacity={0.5}
        position='absolute'
        size='md'
        top='calc(50% - 48px)'
        variant='ghost'
        display={{
          base: 'none',
          xl: 'inline-flex',
        }}
      />
      <IconButton
        ref={nextRef}
        aria-label='Next'
        color='grey.500'
        icon={isRtl ? <ChevronLeftIcon height='36px' width='36px' /> : <ChevronRightIcon height='36px' width='36px' />}
        isDisabled={!isClientReady}
        opacity={0.5}
        position='absolute'
        right='-64px'
        size='md'
        top='calc(50% - 48px)'
        variant='ghost'
        display={{
          base: 'none',
          xl: 'inline-flex',
        }}
      />
    </Box>
  );
}
